/* eslint-disable import/no-unresolved */
import _ from 'underscore';
import {
  DeterministicView,
  type DeterministicViewInstance,
} from '@bingads-webui/deterministic-view';
import AlertListView from '../alert-list/index';
import AlertCarouselView from '../alert-carousel/index';
import template from './template.pug';
import {
  IAlertLevel, IAlert, IAlertCenterInitOptions, IAlertSubView,
} from '../types';

// 3 alert levels defined in HIG, and Info for HIG not defined but info alerts used in feature
// see https://aka.ms/kda3co
const levels: IAlertLevel[] = ['Error', 'Warning', 'Info', 'Confirmation'];
const defaultLevel: IAlertLevel = 'Confirmation';

function fallbackLevel(level: IAlertLevel | undefined): IAlertLevel {
  return level || defaultLevel;
}

function getAlertListChild(alertCenter: AlertCenter, level: IAlertLevel | undefined): AlertListView {
  return alertCenter.getChild(`level(${fallbackLevel(level)})`) as AlertListView;
}

function getAlertListView(
  level: IAlertLevel,
  useCarouselForErrors: boolean,
  i18n: IAlertCenterInitOptions['i18n']
): IAlertSubView {
  const Ctor = level === 'Error' && useCarouselForErrors ? AlertCarouselView : AlertListView;

  return new Ctor({ level, i18n });
}

function toast(this: AlertCenter, message: string, level: IAlertLevel, ...options: unknown[]): void {
  const self = this;
  self.toastId = self.show({
    message,
    level,
    dismissible: true,
  }, self.toastId as string, ...options);

  self.once('rendered', () => {
    window.scrollTo(0, 0);
  });

  clearTimeout(self.toastTimerId);

  self.toastTimerId = setTimeout(() => {
    self.dismiss(self.toastId as string);
  }, 10000);
}

/**
 * The Alert Center view
 */
// eslint-disable-next-line no-redeclare
interface AlertCenter {
  toastId?: string;
  toastTimerId?: ReturnType<typeof setTimeout>;
  show(alert: IAlert, id?: string, ...options: unknown[]): string;
}

type AlertCenterConstructor = new (
  options?: IAlertCenterInitOptions
) => DeterministicViewInstance<Record<string, IAlertLevel>>;

// eslint-disable-next-line no-redeclare
class AlertCenter extends (DeterministicView as AlertCenterConstructor) {
  initialize({
    useCarouselForErrors = false,
    i18n,
  }: IAlertCenterInitOptions = {}) {
    _.each(levels, (level: IAlertLevel) => {
      const alertListView = getAlertListView(level, useCarouselForErrors, i18n);

      this.setChild(`level(${level})`, alertListView);

      this.listenTo(alertListView, 'ignore', (args) => {
        this.trigger('ignore', args);
      });
    });
  }

  get template() {
    return () => template({ levels });
  }

  /**
   * Returns a new alert id which will not conflict with others.
   * Sometimes we won't know when we firstly show an alert.
   * It's hard to use return value of @see {@link AlertCenter#show} as the unique id.
   * Then you can use this method to generate an id.
   * @return {string} The alert id
   * @see {@link AlertCenter#show}
   * @see {@link AlertCenter#dismiss}.
   */
  newAlertId(): string {
    return AlertListView.newId();
  }

  /**
   * Show an alert.
   * @param {Object} alert - The alert plain object.
   * @param {string} alert.message - The alert message, which is a HTML string.
   * @param {string} [alert.id] - The id of alert, which should be generated by
   * @see {@link AlertCenter#newAlertId}, same id will cause old one replaced by new one.
   * @param {string} [alert.level=Confirmation] - The level of alert, which could be
   *   'Error', 'Warning' or 'Confirmation'.
   * @param {boolean} [alert.hasIcon=false] - If these is icon for the alert which is shown
   *   at left side of alert message.
   * @param {HTMLString|AlertCenter~iconCallback} [alert.icon] - The icon template, default to be
   *   ```span.alert__icon``.
   * @param {boolean} [alert.dismissible] - If the alert is dismissible, default to be true
   *   for Confirmation, or false for other levels.
   * @param {object} [alert.action=null] - The alert action, default to not shown.
   * @param {object} [alert.action.message] - The alert action message, which is a plain string.
   * @param {object} [alert.action.callback] - The alert action callback, which will be called
   *   when action button clicked by user.
   * @param {string} [id] - Same id will update old alert.
   * @return {string} The alert id.
   * @see {@link AlertCenter#newAlertId} and
   * @see {@link AlertCenter#dismiss}.
   */
  // eslint-disable-next-line @typescript-eslint/default-param-last
  show(alert: IAlert, id: string = this.newAlertId(), _options?: unknown): string {
    this.model.set(id, fallbackLevel(alert.level));
    return getAlertListChild(this, alert.level).show(alert, id);
  }

  /**
   * Dismiss an alert.
   * @param {string} id - The id of alert, which is returned by @see {@link AlertCenter#show}.
   * @return {void}
   */
  dismiss(id: string): void {
    getAlertListChild(this, this.model.get(id) as IAlertLevel | undefined).dismiss(id);
  }

  /**
   * Dismiss all alerts.
   * @return {void}
   */
  dismissAll(): void {
    _.each(levels, (level: IAlertLevel) => getAlertListChild(this, level).dismissAll());
  }

  /**
   * Handy method which is based on @see {@link AlertCenter#show}, with same options as it.
   * @param {string} [message] - The alert message, which is a HTML string.
   * @param {string} [id] - Same id will update old alert.
   * @return {string} The alert id
   * @see {@link AlertCenter#newAlertId} and
   * @see {@link AlertCenter#dismiss}.
   */
  showError(message: string, id?: string, ...options: unknown[]): string {
    return this.show({
      message,
      level: 'Error',
    }, id as string, ...options);
  }

  /**
   * Handy method which is based on @see {@link AlertCenter#show}, with same options as it.
   * @param {string} [message] - The alert message, which is a HTML string.
   * @param {string} [id] - Same id will update old alert.
   * @return {string} The alert id
   * @see {@link AlertCenter#newAlertId} and
   * @see {@link AlertCenter#dismiss}.
   */
  showWarning(message: string, id?: string, ...options: unknown[]): string {
    return this.show({
      message,
      level: 'Warning',
    }, id as string, ...options);
  }

  /**
   * Handy method which is based on @see {@link AlertCenter#show}, with same options as it.
   * @param {string} [message] - The alert message, which is a HTML string.
   * @param {string} [id] - Same id will update old alert.
   * @return {string} The alert id
   * @see {@link AlertCenter#newAlertId} and
   * @see {@link AlertCenter#dismiss}.
   */
  showInfo(message: string, id?: string, ...options: unknown[]): string {
    return this.show({
      message,
      level: 'Info',
    }, id as string, ...options);
  }

  /**
   * Handy method which is based on @see {@link AlertCenter#show}, with same options as it.
   * @param {string} [message] - The alert message, which is a HTML string.
   * @param {string} [id] - Same id will update old alert.
   * @return {string} The alert id
   * @see {@link AlertCenter#newAlertId} and
   * @see {@link AlertCenter#dismiss}.
   */
  showConfirmation(message: string, id?: string, ...options: unknown[]): string {
    return this.show({
      message,
      level: 'Confirmation',
    }, id as string, ...options);
  }

  /**
   * Toast given message as an error.
   * @param {string} message - The alert message, which is a HTML string.
   * @return {void}
   */
  toastError(message: string, ...options: unknown[]): void {
    toast.call(this, message, 'Error', ...options);
  }

  /**
   * Toast given message as a warning.
   * @param {string} message - The alert message, which is a HTML string.
   * @return {void}
   */
  toastWarning(message: string, ...options: unknown[]): void {
    toast.call(this, message, 'Warning', ...options);
  }

  /**
   * Toast given message as an info.
   * @param {string} message - The alert message, which is a HTML string.
   * @return {void}
   */
  toastInfo(message: string, ...options: unknown[]): void {
    toast.call(this, message, 'Info', ...options);
  }

  /**
   * Toast given message as a confirmation.
   * @param {string} message - The alert message, which is a HTML string.
   * @return {void}
   */
  toastConfirmation(message: string, ...options: unknown[]): void {
    toast.call(this, message, 'Confirmation', ...options);
  }
}

/**
 * @callback AlertCenter~iconCallback
 * @param {Object} alert - the alert passed in.
 * @return {HTMLString} the icon template.
 */

export default AlertCenter;
